<template>
  <div class="container px-4">
    <div
      ref="blocks"
      class="flex items-center flex-wrap justify-between mb-4 lg:mb-6 pt-10 lg:pt-20"
      v-if="showMap || title || showSort || showFilters || hasListingFilters"
    >

      <div class="w-full mb-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2" v-if="title">
        <h3 class="text-2xl lg:text-3xl text-left">
          <a :id="anchor" v-if="anchor" class="anchor"></a>
          {{ title }}
        </h3>
        <a
          v-if="hasBirdItems"
          href="/learn/birds-of-prey/"
          class="inline-flex items-center gap-2 text-dark font-semibold hover:underline"
        >
          <span>All Birds of Prey</span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="flex-shrink-0">
            <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
      </div>

      <div v-if="pageType !== 'experience'" class="flex flex-col lg:flex-row lg:justify-between lg:items-center w-full">
        <div :class="{ 'order-last lg:order-none mt-3 lg:mt-0': hasListingFilters }">
          <sorts v-if="showSort" :orders="orders" v-model="order" :name="`sort_${name}`" @sort="sort"></sorts>

          <div v-if="showMap" class="lg:flex lg:flex-row lg:items-center lg:justify-between">
            <div class="flex items-center lg:justify-start gap-1" :class="hasListingFilters ? 'justify-between lg:max-w-sm' : 'justify-center max-w-sm'">
              <span v-if="hasListingFilters" class="lg:hidden text-sm text-grey">{{ resultsCount }}</span>

              <a
                  class="hidden lg:flex flex-1 lg:flex-auto justify-center items-center gap-2 px-4 py-3 border rounded-full text-sm font-medium"
                  :class="view === 'list' ? 'bg-light hover:bg-white border-default' : 'bg-white hover:bg-light border-light'"
                  href="#"
                  @click.prevent="view = 'list'"
              >
                  <svg width="18" height="15" viewBox="0 0 18 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.125 0.1875H2.8125C3.26953 0.1875 3.65625 0.574219 3.65625 1.03125V2.71875C3.65625 3.21094 3.26953 3.5625 2.8125 3.5625H1.125C0.632812 3.5625 0.28125 3.21094 0.28125 2.71875V1.03125C0.28125 0.574219 0.632812 0.1875 1.125 0.1875ZM6.1875 1.03125H16.875C17.332 1.03125 17.7188 1.41797 17.7188 1.875C17.7188 2.36719 17.332 2.71875 16.875 2.71875H6.1875C5.69531 2.71875 5.34375 2.36719 5.34375 1.875C5.34375 1.41797 5.69531 1.03125 6.1875 1.03125ZM6.1875 6.65625H16.875C17.332 6.65625 17.7188 7.04297 17.7188 7.5C17.7188 7.99219 17.332 8.34375 16.875 8.34375H6.1875C5.69531 8.34375 5.34375 7.99219 5.34375 7.5C5.34375 7.04297 5.69531 6.65625 6.1875 6.65625ZM6.1875 12.2812H16.875C17.332 12.2812 17.7188 12.668 17.7188 13.125C17.7188 13.6172 17.332 13.9688 16.875 13.9688H6.1875C5.69531 13.9688 5.34375 13.6172 5.34375 13.125C5.34375 12.668 5.69531 12.2812 6.1875 12.2812ZM0.28125 6.65625C0.28125 6.19922 0.632812 5.8125 1.125 5.8125H2.8125C3.26953 5.8125 3.65625 6.19922 3.65625 6.65625V8.34375C3.65625 8.83594 3.26953 9.1875 2.8125 9.1875H1.125C0.632812 9.1875 0.28125 8.83594 0.28125 8.34375V6.65625ZM1.125 11.4375H2.8125C3.26953 11.4375 3.65625 11.8242 3.65625 12.2812V13.9688C3.65625 14.4609 3.26953 14.8125 2.8125 14.8125H1.125C0.632812 14.8125 0.28125 14.4609 0.28125 13.9688V12.2812C0.28125 11.8242 0.632812 11.4375 1.125 11.4375Z" fill="#3B3C4A"/>
                  </svg>

                  <span class="relative top-0.5">List View</span>
              </a>

              <a
                  class="flex lg:flex-auto justify-center items-center gap-2 px-4 border rounded-full text-sm font-medium"
                  :class="[
                    // The active view gets the same ochre border as a filter in use
                    view === 'map' ? 'bg-light hover:bg-white border-default' : 'bg-light hover:bg-white lg:bg-white lg:hover:bg-light border-light',
                    hasListingFilters ? 'py-2 lg:py-3' : 'flex-1 py-3',
                  ]"
                  href="#"
                  @click.prevent="mobileToggleMapView()"
              >
                  <svg width="19" height="15" viewBox="0 0 19 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18.1562 0.640625C18.375 0.765625 18.5 1.01562 18.5 1.23438V11.7344C18.5 12.0469 18.2812 12.3281 18 12.4531L12.75 14.4531C12.5938 14.5156 12.4062 14.5156 12.25 14.4531L6.5 12.5469L1.5 14.4531C1.28125 14.5469 1 14.5156 0.8125 14.3594C0.59375 14.2344 0.5 13.9844 0.5 13.7344V3.23438C0.5 2.92188 0.6875 2.67188 0.96875 2.54688L6.21875 0.546875C6.375 0.484375 6.5625 0.484375 6.71875 0.546875L12.4688 2.45312L17.4688 0.546875C17.6875 0.453125 17.9688 0.484375 18.1562 0.640625ZM2 3.76562V12.6719L5.75 11.2344V2.32812L2 3.76562ZM11.75 12.7031V3.79688L7.25 2.29688V11.2031L11.75 12.7031ZM13.25 12.6719L17 11.2344V2.32812L13.25 3.76562V12.6719Z" fill="#3B3C4A"/>
                  </svg>

                  <span class="relative top-0.5">View On Map</span>
              </a>
            </div>
          </div>

        </div>

        <div v-if="hasListingFilters" class="grid grid-flow-col auto-cols-fr gap-2 lg:flex lg:items-center lg:gap-4 text-base font-regular">
          <filter-select
            v-if="types.length > 1"
            :id="`${name}_show`"
            label="Show"
            all-label="All experiences"
            :options="types"
            v-model="show"
          ></filter-select>

          <filter-select
            v-if="areas.length > 1"
            :id="`${name}_near`"
            label="Near"
            all-label="Anywhere"
            :options="areas"
            v-model="near"
          ></filter-select>
        </div>
      </div>

      <div class="relative flex">
        <filters
          v-if="showFilters"
          :results="results"
          :name="`filters_${name}`"
          :config="configuration"
          :value="filters"
          @input="filter"
          @clear="clearFilters()"
        ></filters>
      </div>
    </div>

    <div v-if="view === 'map'" ref="backToListView" class="-mx-4 md:mx-0 lg:mb-16 pt-22 lg:pt-0">
      <a class="lg:hidden flex items-center gap-2 h-10 py-3 px-4 bg-light leading-none font-medium text-sm" href="#" @click.prevent="mobileToggleListView()">
        <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0.77252 7.19684L7.62202 0.347338C8.05011 -0.11643 8.79928 -0.11643 9.22737 0.347338C9.69114 0.775431 9.69114 1.5246 9.22737 1.95269L3.19838 8.01735L9.22737 14.0463C9.69114 14.4744 9.69114 15.2236 9.22737 15.6517C8.79927 16.1155 8.05011 16.1155 7.62202 15.6517L0.77252 8.80219C0.308752 8.37409 0.308752 7.62493 0.77252 7.19684Z" fill="#3B3C4A"/>
        </svg>

        <span class="relative top-0.5 tracking-wide">Back to List View</span>
      </a>

      <!-- The map only reads its attractions once, so rebuild it when the Show/Near filters change -->
      <related-map :key="`${show}|${near}`" :attractions="results.data.items"></related-map>
    </div>

    <div v-if="view === 'list'" class="lg:mb-16">
      <div class="flex flex-wrap -mx-4 gap-y-4 lg:gap-y-6" v-if="hasResults">
        <card
          v-for="item in results.data.items"
          :key="item.id"
          :item="item"
          :map-toggle="showMap"
          :one-column-on-tablets="false"
          class="sm:w-1/2 lg:w-1/3"
        ></card>

        <gift v-if="showGift && !listingFiltered"></gift>
      </div>

      <div class="flex flex-wrap px-5 2xl:px-0 -mx-4" v-if="!hasResults">
        <p class="px-5" v-if="listingFiltered">
          Nothing matches those filters.
          <a class="underline" href="#" @click.prevent="clearListingFilters()">Show all experiences</a>
        </p>
        <p class="px-5" v-else>Nothing found, sorry.</p>
      </div>

      <pagination
        v-if="paginate && results.pagination.total > results.pagination.per_page"
        :page-count="Math.ceil(results.pagination.total / results.pagination.per_page)"
        :current-page="page"
        :click-handler="goToPage"
        :prev-text="'Prev'"
        :next-text="'Next'"
        :container-class="'pagination'"
      >
      </pagination>
    </div>

  </div>
</template>
<script>
import itemsjs from "./../../plugins/itemsjs"
import VLazyImage from "./../../plugins/v-lazy-image/dist/v-lazy-image";
import Card from "./../Card"
import Gift from "./../Gift"
import Filters from "./../facets/Filters"
import FilterSelect from "./../facets/FilterSelect"
import Sorts from "./../facets/Sorts"
import Pagination from "./../Pagination.vue"

import OrderMixin from "./../../mixins/order"
import FiltersMixin from "./../../mixins/filters"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import ResponsiveMixin from "../../mixins/responsive"
import isEmpty from "lodash/isEmpty"
import { trackEvent } from "../../helpers/track"
import {defineAsyncComponent} from "vue"; // dependent on utc plugin
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault("Australia/Perth")

// Mapbox is most of the site's JavaScript, so only download it when someone opens the map
const RelatedMap = defineAsyncComponent(() => import("./RelatedMap.vue"))

export default {
  components: {
    VLazyImage,
    Sorts,
    Filters,
    FilterSelect,
    Card,
    Gift,
    RelatedMap,
    Pagination,
  },
  mixins: [OrderMixin, FiltersMixin, ResponsiveMixin],
  props: {
    name: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: "normal",
    },
    pageType: {
      type: String,
      default: "default",
    },
    title: {
      type: String,
      default: "",
    },
    anchor: {
      type: String,
      default: "",
    },
    showMap: {
      type: Boolean,
      default: true,
    },
    showSort: {
      type: Boolean,
      default: false,
    },
    showGift: {
      type: Boolean,
      default: false,
    },
    showFilters: {
      type: Boolean,
      default: false,
    },
    paginate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    let aggregations = {}

    if (window?.capes?.[this.name]?.filters?.categories) {
      aggregations.categories = {
        title: "Categories",
        size: 100,
        sort: "key",
        limit: window.capes[this.name].filters.categories,
      }
    }

    if (window?.capes?.[this.name]?.filters?.tags) {
      aggregations.tags = {
        title: "Tags",
        size: 100,
        sort: "key",
        limit: window.capes[this.name].filters.tags,
      }
    }

    let items = window?.capes?.[this.name].items ?? []

    return {
      configuration: {
        //searchableFields: ['tags', 'categories'],
        sortings: {
          relevance: {
            field: "relevance",
            order: "asc",
          },
        },
        aggregations: aggregations,
      },
      visitorCenters: this.$settings.visitor_centres,
      items: items,
      itemsjs: {},
      filters: {},
      types: window?.capes?.[this.name]?.filters?.types ?? [],
      areas: window?.capes?.[this.name]?.filters?.areas ?? [],
      show: "",
      near: "",
      query: "",
      page: 1,
      per_page: this.paginate ? 12 : 1000,
      date: "",
      view: "list",
    }
  },
  computed: {
    hasResults() {
      return this.results?.data?.items ? this.results.data.items.length > 0 : false
    },
    hasListingFilters() {
      return this.types.length > 1 || this.areas.length > 1
    },
    listingFiltered() {
      return this.show !== "" || this.near !== ""
    },
    resultsCount() {
      // Leave out plain pages such as the Gift Cards card
      const count = (this.results?.data?.items ?? []).filter((item) => item.type !== "page").length
      return `${count} ${count === 1 ? "experience" : "experiences"}`
    },
    hasBirdItems() {
      // Check if all items in the results are birds
      if (!this.results?.data?.items || this.results.data.items.length === 0) return false
      return this.results.data.items.every(item => item.type === 'bird')
    },
    eventDates() {
      return window.capes[this.name].filters.dates
    },
    results() {
      return this.itemsjs
        ? this.itemsjs.search({
            query: this.query,
            sort: this.orders[this.order],
            filters: this.filters,
            page: this.page,
            per_page: this.per_page,
            date: this.date,
            prefilter(items) {
              return items.filter((item) => {
                return item.dates.includes(dayjs(this.date).format("YYYY-MM-DD"))
              })
            },
          })
        : []
    },
  },
  methods: {
    // Items matching the Show (category) and Near (area) dropdowns
    listingItems() {
      // The URL uses the plural label (caves), the cards have the category slug (cave)
      const slug = this.types.find((option) => option.value === this.show)?.slug

      return this.items.filter((item) => {
        if (this.show && !(item.categories || []).some((term) => term.slug === slug)) {
          return false
        }

        return !this.near || item.area === this.near
      })
    },
    clearListingFilters() {
      this.show = ""
      this.near = ""
      trackEvent("Filter cleared")
    },
    applyListingFilters() {
      this.page = 1
      this.itemsjs = itemsjs(this.listingItems(), this.configuration)

      // Keep the choice in the URL so it can be linked to, e.g. /visit-experiences/?show=cave
      const url = new URL(window.location.href)
      ;["show", "near"].forEach((key) => (this[key] ? url.searchParams.set(key, this[key]) : url.searchParams.delete(key)))
      window.history.replaceState(window.history.state, "", url)
    },
    goToPage(page) {
      this.page = page

      this.$refs.blocks.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      })
    },
    mobileToggleMapView() {
      this.view = "map";

      if( this.mobile ){
        this.$nextTick(() => {
          this.$refs.backToListView.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          })
        })
      
        this.disableScroll()
      }
    },
    mobileToggleListView() {
      this.view = "list"
      
      this.enableScroll()
    },
    disableScroll() {
      document.body.classList.add('overflow-hidden');
      document.body.addEventListener('touchmove', this.preventScroll, { passive: false });
    },
    enableScroll() {
      document.body.classList.remove('overflow-hidden');
      document.body.removeEventListener('touchmove', this.preventScroll);
    },
    preventScroll(event) {
      const scrollableElement = document.querySelector('.modal-scrollable');
      if (scrollableElement && scrollableElement.contains(event.target)) {
        return;
      }

      event.preventDefault();
    },
  },
  created() {
    this.clearFilters()
    this.buildDistance()
    this.buildSorting()

    const params = new URLSearchParams(window.location.search)
    if (this.types.some((option) => option.value === params.get("show"))) {
      this.show = params.get("show")
    }
    if (this.areas.some((option) => option.value === params.get("near"))) {
      this.near = params.get("near")
    }

    this.itemsjs = itemsjs(this.listingItems(), this.configuration)

    // console.log( this.items )

    this.watchFilters()
    this.$watch(() => [this.show, this.near], this.applyListingFilters)
  },
}
</script>
